import { useState } from 'react'
import Card from './ui/Card'
import Icon from './ui/Icon'
import Button from './ui/Button'

const Calendar = ({ calendarData, onMonthChange }) => {
  const [currentDate, setCurrentDate] = useState(new Date(calendarData.currentYear, calendarData.currentMonth))
  
  const monthNames = [
    'জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন',
    'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর'
  ]
  
  const weekdays = ['রবি', 'সোম', 'মঙ্গল', 'বুধ', 'বৃহঃ', 'শুক্র', 'শনি']
  
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }
  
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }
  
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate)
    newDate.setMonth(currentDate.getMonth() + direction)
    setCurrentDate(newDate)
    
    // Fetch new calendar data for the selected month
    if (onMonthChange) {
      onMonthChange(newDate.getMonth() + 1, newDate.getFullYear())
    }
  }
  
  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate)
    const firstDay = getFirstDayOfMonth(currentDate)
    const days = []
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-10 h-10"></div>)
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isWakeUpDay = calendarData.wakeUpDays.includes(day)
      const isToday = day === new Date().getDate() && 
                     currentDate.getMonth() === new Date().getMonth() &&
                     currentDate.getFullYear() === new Date().getFullYear()
      
      days.push(
        <div
          key={day}
          className={`w-10 h-10 flex items-center justify-center text-sm rounded-lg cursor-pointer transition-all ${
            isToday 
              ? 'bg-primary text-white font-bold ring-2 ring-primary/50' 
              : isWakeUpDay 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 font-semibold hover:bg-green-200 dark:hover:bg-green-800' 
                : 'text-muted-foreground hover:bg-accent/50'
          }`}
          title={isWakeUpDay ? 'ফজরে জেগেছিলেন' : 'ফজরে জাগেননি'}
        >
          {day}
        </div>
      )
    }
    
    return days
  }
  
  const progressPercentage = Math.round((calendarData.thisMonthTotal / calendarData.monthlyGoal) * 100)
  
  return (
    <Card className="p-6">
      <div className="flex items-center justify-center space-x-2 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateMonth(-1)}
          className="p-2"
        >
          <Icon name="ArrowLeft" className="w-4 h-4" />
        </Button>
        <span className="font-semibold min-w-32 text-center">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateMonth(1)}
          className="p-2"
        >
          <Icon name="ArrowRight" className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Calendar Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-accent/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-secondary">{calendarData.thisMonthTotal}</div>
          <div className="text-sm text-muted-foreground">এ মাসে জেগেছেন</div>
        </div>
        <div className="bg-accent/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600">{calendarData.streakCount}</div>
          <div className="text-sm text-muted-foreground">বর্তমান স্ট্রিক</div>
        </div>
        <div className="bg-accent/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-blue-600">{calendarData.bestStreak}</div>
          <div className="text-sm text-muted-foreground">সর্বোচ্চ স্ট্রিক</div>
        </div>
        <div className="bg-accent/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-purple-600">{progressPercentage}%</div>
          <div className="text-sm text-muted-foreground">লক্ষ্য অর্জন</div>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-muted-foreground mb-2">
          <span>মাসিক লক্ষ্য অগ্রগতি</span>
          <span>{calendarData.thisMonthTotal}/{calendarData.monthlyGoal} দিন</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-gradient-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
          ></div>
        </div>
      </div>
      
      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekdays.map(day => (
          <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {renderCalendarDays()}
      </div>
      
      {/* Legend */}
      <div className="flex justify-center items-center space-x-6 mt-6 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-green-100 dark:bg-green-900 rounded"></div>
          <span className="text-sm text-muted-foreground">জেগেছেন</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-primary rounded"></div>
          <span className="text-sm text-muted-foreground">আজ</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-muted rounded"></div>
          <span className="text-sm text-muted-foreground">জাগেননি</span>
        </div>
      </div>
    </Card>
  )
}

export default Calendar